import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { LogOut, Settings, CheckCircle2, Circle, ChevronRight, LayoutDashboard, KeyRound } from "lucide-react";
import ReminderPopup from "../components/ReminderPopup";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardPage = () => {
  const { user, logout, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  
  // Calculate default month: previous month (if January, show December of previous year)
  const getDefaultMonth = () => {
    if (currentMonth === 1) {
      return 12; // December
    }
    return currentMonth - 1;
  };
  
  const getDefaultYear = () => {
    if (currentMonth === 1) {
      return currentYear - 1; // Previous year if we're in January
    }
    return currentYear;
  };
  
  const [selectedYear, setSelectedYear] = useState(getDefaultYear());
  const [validYears, setValidYears] = useState([currentYear - 1, currentYear]);
  const [yearData, setYearData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Change password state
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);

  const handleReminderUpload = () => {
    // Navigate to the default month (previous month)
    navigate(`/dashboard/${getDefaultYear()}/${getDefaultMonth()}`);
  };

  useEffect(() => {
    fetchValidYears();
  }, []);

  useEffect(() => {
    fetchYearStatus();
  }, [selectedYear]);

  const fetchValidYears = async () => {
    try {
      const response = await axios.get(`${API}/years`, {
        headers: getAuthHeader()
      });
      setValidYears(response.data.years);
      setSelectedYear(response.data.current_year);
    } catch (error) {
      // Fallback to current and previous year
      setValidYears([currentYear - 1, currentYear]);
    }
  };

  const fetchYearStatus = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/documents/status/${selectedYear}`, {
        headers: getAuthHeader()
      });
      setYearData(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleMonthClick = (month) => {
    navigate(`/dashboard/${selectedYear}/${month}`);
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error("Le password non coincidono");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("La nuova password deve avere almeno 6 caratteri");
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, {
        headers: getAuthHeader()
      });
      
      toast.success("Password cambiata con successo!");
      setPasswordDialog(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel cambio password");
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="border-b border-neutral-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-[#EDEDED]">Il Commercialista</h1>
            <p className="text-[#D4AF37] text-xs uppercase tracking-[0.2em]">come partner strategico</p>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-[#A1A1AA] text-sm hidden sm:block">
              Benvenuto, <span className="text-[#EDEDED]">{user?.name}</span>
            </span>
            {user?.is_admin && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate("/admin/dashboard")}
                  className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black"
                  data-testid="admin-dashboard-btn"
                >
                  <LayoutDashboard className="w-4 h-4 mr-2" />
                  Dashboard Admin
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/admin")}
                  className="text-[#A1A1AA] hover:text-[#D4AF37]"
                  data-testid="admin-btn"
                  title="Gestione Utenti"
                >
                  <Settings className="w-4 h-4" />
                </Button>
              </>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPasswordDialog(true)}
              className="text-[#A1A1AA] hover:text-[#D4AF37]"
              data-testid="change-password-btn"
              title="Cambia Password"
            >
              <KeyRound className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-[#A1A1AA] hover:text-[#D4AF37]"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Reminder Popup */}
      <ReminderPopup onGoToUpload={handleReminderUpload} />

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Year Selector */}
        <div className="mb-8">
          <Tabs value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
            <TabsList className="bg-[#0A0A0A] border border-neutral-800">
              {validYears.map((year) => (
                <TabsTrigger 
                  key={year}
                  value={String(year)} 
                  className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black"
                  data-testid={`year-tab-${year}`}
                >
                  {year}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        {/* Month Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {yearData?.months.map((month) => {
              const isCurrentMonth = selectedYear === currentYear && month.month === currentMonth;
              const allUploaded = month.uploaded_count === month.total_categories;
              const hasUploads = month.uploaded_count > 0;
              
              return (
                <button
                  key={month.month}
                  onClick={() => handleMonthClick(month.month)}
                  className={`
                    card-month text-left group cursor-pointer
                    ${isCurrentMonth ? 'border-[#D4AF37] ring-1 ring-[#D4AF37]/20' : ''}
                    ${allUploaded ? 'border-[#10B981]/50' : ''}
                  `}
                  data-testid={`month-card-${month.month}`}
                >
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-lg font-serif text-[#EDEDED]">{month.month_name}</h3>
                      {isCurrentMonth && (
                        <span className="text-[10px] uppercase tracking-wider text-[#D4AF37] bg-[#D4AF37]/10 px-2 py-1 rounded">
                          Corrente
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[#52525B] font-mono">{selectedYear}</p>
                  </div>

                  <div>
                    {/* Progress bar */}
                    <div className="mb-3">
                      <div className="h-1 bg-neutral-800 rounded-full overflow-hidden">
                        <div 
                          className={`h-full transition-all duration-500 ${allUploaded ? 'bg-[#10B981]' : 'bg-[#D4AF37]'}`}
                          style={{ width: `${(month.uploaded_count / month.total_categories) * 100}%` }}
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className={`flex items-center gap-2 text-xs ${allUploaded ? 'text-[#10B981]' : hasUploads ? 'text-[#D4AF37]' : 'text-neutral-500'}`}>
                        {allUploaded ? (
                          <CheckCircle2 className="w-4 h-4" />
                        ) : (
                          <Circle className="w-4 h-4" />
                        )}
                        <span className="font-mono">{month.uploaded_count}/{month.total_categories}</span>
                      </div>
                      <ChevronRight className="w-4 h-4 text-neutral-600 group-hover:text-[#D4AF37] transition-colors" />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Legend */}
        <div className="mt-12 flex flex-wrap gap-6 justify-center text-xs">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#10B981]" />
            <span className="text-[#A1A1AA]">Completo</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#D4AF37]" />
            <span className="text-[#A1A1AA]">In corso</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-neutral-600" />
            <span className="text-[#A1A1AA]">Da completare</span>
          </div>
        </div>
      </main>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialog} onOpenChange={setPasswordDialog}>
        <DialogContent className="bg-[#0A0A0A] border border-neutral-800">
          <DialogHeader>
            <DialogTitle className="text-[#EDEDED] flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-[#D4AF37]" />
              Cambia Password
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Inserisci la password attuale e quella nuova
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="current-password" className="text-[#A1A1AA] text-sm">
                Password Attuale
              </Label>
              <Input
                id="current-password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="mt-2 bg-neutral-900 border-neutral-700 text-[#EDEDED]"
                data-testid="current-password-input"
              />
            </div>
            <div>
              <Label htmlFor="new-password" className="text-[#A1A1AA] text-sm">
                Nuova Password
              </Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Minimo 6 caratteri"
                className="mt-2 bg-neutral-900 border-neutral-700 text-[#EDEDED]"
                data-testid="new-password-input"
              />
            </div>
            <div>
              <Label htmlFor="confirm-password" className="text-[#A1A1AA] text-sm">
                Conferma Nuova Password
              </Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="mt-2 bg-neutral-900 border-neutral-700 text-[#EDEDED]"
                data-testid="confirm-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setPasswordDialog(false);
                setCurrentPassword("");
                setNewPassword("");
                setConfirmPassword("");
              }}
              className="text-[#A1A1AA]"
            >
              Annulla
            </Button>
            <Button
              onClick={handleChangePassword}
              disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
              className="bg-[#D4AF37] text-black hover:bg-[#B5952F]"
              data-testid="save-password-btn"
            >
              {changingPassword ? (
                <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
              ) : (
                "Salva"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardPage;
